/**
 * The pluggable-engine contract.
 *
 * ## Why this exists at all
 *
 * ADR §4 chose a hand-written FFT → harmonic chroma → template match pipeline over a
 * library or a learned model, and gave reasons. Those reasons are still good, but the
 * choice has never been *measured* against the alternative on a real piano — only argued.
 * Rather than keep arguing it, several engines ship side by side so the question can be
 * settled at the instrument, in the room, with the actual instrument's actual spectra.
 *
 * ## What an engine is, and what it is not
 *
 * An engine is a `RecognitionEngineCore`: the same pure `processFrame(Float32Array) =>
 * RecognizerEvent[]` shape the DSP core already has. It is NOT a `ChordRecognizer` —
 * nothing here owns an `AudioContext`, a worklet or a microphone. That separation is what
 * makes comparison possible: because a core is pure and cheap, one stream of audio frames
 * can be fanned out to three of them at once and their answers laid side by side. Swapping
 * engines and relying on memory is not a comparison.
 *
 * ## Why `load()` is separate from the factory
 *
 * `load()` is async and may reject; the factory it resolves to is synchronous. Everything
 * expensive and everything that can fail — dynamic `import()`, WASM instantiation, probing
 * whether the library can run in this browser at all — happens once, in `load()`. By the
 * time a core is constructed the engine is known to work. A `load()` that rejects marks
 * the engine unavailable with a reason the UI shows; it never breaks the session.
 */

import type { RecognizerEvent, RecognizerParams } from '@hc/shared';

/**
 * The three engines. A union rather than a string so that a persisted profile naming an
 * engine that no longer exists fails a type-guard instead of silently selecting nothing.
 */
export type RecognizerEngineId = 'custom' | 'meyda-chroma' | 'essentia';

export const RECOGNIZER_ENGINE_IDS = ['custom', 'meyda-chroma', 'essentia'] as const;

/** The default, and the only engine with no download cost. */
export const DEFAULT_ENGINE_ID: RecognizerEngineId = 'custom';

export function isRecognizerEngineId(value: unknown): value is RecognizerEngineId {
  return typeof value === 'string' && (RECOGNIZER_ENGINE_IDS as readonly string[]).includes(value);
}

/**
 * Structurally identical to `RecognizerCore` in `audio/coreAdapter.ts` — deliberately, so
 * an engine drops into the existing audio path with no adapter — plus one optional extra.
 */
export interface RecognitionEngineCore {
  /** Called once per audio block. Must not block and must not throw. */
  processFrame(samples: Float32Array): RecognizerEvent[];
  getParams(): RecognizerParams;
  setParams(patch: Partial<RecognizerParams>): void;
  /** Called when the audio graph restarts, so history does not straddle it. */
  reset?(): void;
  /**
   * One line of engine-specific colour recorded alongside each emission — what the
   * library's *own* chord detector said, a WASM failure count, whatever the engine can
   * usefully add. Shown in the comparison panel and carried into the export.
   */
  describeSecondOpinion?(): string | null;
}

export interface EngineFactoryOptions {
  /** From `AudioContext.sampleRate`. Never assumed. */
  sampleRate: number;
  params: RecognizerParams;
  /**
   * `performance.now()` when the graph started. Every engine in a comparison gets the
   * same origin, which is what makes their `DetectedChord.timestamp`s comparable.
   */
  timeOriginMs: number;
}

export type RecognizerEngineFactory = (opts: EngineFactoryOptions) => RecognitionEngineCore;

export interface RecognizerEngineDescriptor {
  id: RecognizerEngineId;
  /** Shown in the picker. */
  label: string;
  /** One line: what this engine is testing. */
  blurb: string;
  /** So the user knows what a switch costs to download. 0 for the built-in. */
  approxWeightKb: number;
  /** Lazily import and construct. Rejects if the engine cannot run here. */
  load(): Promise<RecognizerEngineFactory>;
}

/** What the picker and the comparison panel need to know about one engine right now. */
export type EngineStatus = 'idle' | 'loading' | 'ready' | 'unavailable';

export interface EngineLoadState {
  id: RecognizerEngineId;
  status: EngineStatus;
  /** Present only when `status === 'unavailable'`. Shown verbatim in the UI. */
  reason?: string;
}
